'use strict';

/** Words a note surface shows before clipping; the editor always shows all of it. */
export const NOTE_PREVIEW_WORDS = 200;

/**
 * Clips text to the note preview length, marking the cut with " [...]". Text at or
 * under the limit is returned untouched, and the kept part is sliced out of the
 * original rather than rebuilt from its words, so its line breaks survive.
 * @param {string} text - The note or passage to clip
 * @returns {string}
 */
export function clipToPreviewWords(text) {
    const source = text || '';
    const words = source.match(/\S+/g);
    if (!words || words.length <= NOTE_PREVIEW_WORDS) return source;

    let cut = 0;
    for (let index = 0; index < NOTE_PREVIEW_WORDS; index++) {
        cut = source.indexOf(words[index], cut) + words[index].length;
    }

    return `${source.slice(0, cut)} [...]`;
}

/**
 * Clips rendered markup the way clipToPreviewWords clips text: the first
 * NOTE_PREVIEW_WORDS words stay, with the formatting around them, the cut is marked
 * with " [...]" right after the last kept word, and everything past it goes, so no
 * emptied list or paragraph is left behind. Markup at or under the limit is returned
 * untouched, as is markup without words (an embed-only note).
 * @param {string} html - The note's sanitized markup
 * @returns {string}
 */
export function clipHtmlToPreviewWords(html) {
    const source = html || '';
    if (!source) return source;

    const parsed = new DOMParser().parseFromString(source, 'text/html');
    const textNodes = parsed.createTreeWalker(parsed.body, NodeFilter.SHOW_TEXT);
    let remaining = NOTE_PREVIEW_WORDS;
    let lastKept = null;
    let cutAt = null;

    for (let node = textNodes.nextNode(); node; node = textNodes.nextNode()) {
        const words = node.nodeValue.match(/\S+/g);
        if (!words) continue;

        if (words.length <= remaining) {
            remaining -= words.length;
            lastKept = node;
            continue;
        }

        // The limit falls inside this node, or right before it: the marker goes after
        // the last word that stays, wherever that word is.
        if (remaining === 0) {
            cutAt = lastKept;
            cutAt.nodeValue = `${cutAt.nodeValue.replace(/\s+$/, '')} [...]`;
        } else {
            let cut = 0;
            for (let index = 0; index < remaining; index++) {
                cut = node.nodeValue.indexOf(words[index], cut) + words[index].length;
            }
            node.nodeValue = `${node.nodeValue.slice(0, cut)} [...]`;
            cutAt = node;
        }
        break;
    }

    if (!cutAt) return source;

    // Everything after the cut, at every level up to the body, goes.
    for (let node = cutAt; node && node !== parsed.body; node = node.parentNode) {
        while (node.nextSibling) node.nextSibling.remove();
    }

    return parsed.body.innerHTML;
}
